import { GridApi } from "ag-grid-community";
import lodashIsEqual from "lodash/isEqual";
import memoizeOne from "memoize-one";
import { fromJS, List, Map } from "immutable";
import { GroupKeyType, IGroupKey } from "components/infinite";
import { Item, Kinds } from "constants/product-collection";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { amphibiousGetIn } from "helpers/objUtils";
import { SchemaKeyMap } from "helpers/product-collection";
import { formatMessage } from "lib/globalize-util";

const reduceSchemaListToMap = (schemaList: List<SchemaKeyMap>): Map<string, SchemaKeyMap> =>
    schemaList.reduce((acc, schemaKey) => acc.set(schemaKey.get("field_name"), schemaKey), Map());

/**
 * Helper function to group assortment items for usage in Tile View
 */
export const groupItems = memoizeOne(
    (
        items: List<any>,
        schemaList: List<SchemaKeyMap>,
        allocationDimensions: IAllocationDimension[],
        groupKeys: IGroupKey[],
        gridApi: GridApi,
    ) => {
        const currentSchema = reduceSchemaListToMap(schemaList);

        const array = [];

        items.forEach((data) => {
            const groupKeyValues: string[] = groupKeys.map((key) => {
                // Group by is of 3 types - allocation dimension, formula and string value.
                // if grouping is by allocation dimension, then group each item by the allocation_dimension ids
                if (key.type === GroupKeyType.AllocationDimension) {
                    const allocationDimensionIndex = allocationDimensions.findIndex((allocationDimension) => {
                        return allocationDimension.id === key.key;
                    });

                    const dimensionValueId = amphibiousGetIn(data, ["breadcrumbs", allocationDimensionIndex]);
                    const allocationDimensionValue = allocationDimensions[
                        allocationDimensionIndex
                    ].dimension_values.find((dimensionValue) => {
                        return dimensionValue.id === dimensionValueId;
                    });

                    return allocationDimensionValue?.name || formatMessage("N/A");
                }

                const currentFieldName = key.key[1];
                const schemaKey = currentSchema.get(currentFieldName)?.toJS();

                let headerValue;
                if (schemaKey && schemaKey.kind === Kinds.FORMULA) {
                    const rowId = amphibiousGetIn(data, Item.FIELDS.ID);
                    const rowNode = gridApi.getRowNode(rowId);
                    const valueFromGrid = gridApi.getValue(currentFieldName, rowNode);

                    headerValue = valueFromGrid;
                } else {
                    headerValue = amphibiousGetIn(data, key.key);
                }

                return headerValue || formatMessage("N/A");
            });

            const upperCaseGroupKeyValues = groupKeyValues.map((v) => (typeof v === "string" ? v.toUpperCase() : v));

            const obj = array.find((grouping) => {
                return lodashIsEqual(grouping.groupKeyValues, upperCaseGroupKeyValues);
            });

            if (obj) {
                obj.items.push(data);
            } else {
                array.push({
                    groupKeyValues: upperCaseGroupKeyValues,
                    items: [data],
                });
            }
        });

        return fromJS(array);
    },
);
